import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { Card, EmptyState } from "@/components/ui";
import { useAuthStore } from "@/stores/authStore";

import { homePathFor } from "./homePath";

/**
 * Route guards.
 *
 * These hide what a user cannot reach. They are **not** the enforcement point —
 * that is always the server, which checks the same permission on every request.
 * A guard that was bypassed would reveal an empty screen, not data.
 */

/**
 * The shop's own screens. Platform staff reach them only inside a support view;
 * otherwise they have no shop to show, and go to the admin portal instead. A
 * personal account has no shop either, and goes to the stokvel portal.
 */
export function RequireShopAccess({ children }: { children: ReactNode }) {
  const session = useAuthStore((s) => s.session);

  if (session?.user.isPersonal) return <Navigate to="/stokvel" replace />;
  if (session?.user.isPlatformStaff && !session.supportView) {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
}

/** The index route: the right home for whoever is signed in. */
export function HomeRedirect() {
  const session = useAuthStore((s) => s.session);
  return <Navigate to={homePathFor(session)} replace />;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  // "loading" is the brief moment while the session is read from IndexedDB.
  // Redirecting during it would bounce every reload through the login screen.
  if (status === "loading") return <FullScreenSpinner />;

  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

/**
 * The SmartSpaza administration portal is for platform staff only.
 *
 * Redirects rather than explains, unlike `RequirePermission`. A merchant's
 * cashier who lands on /admin has not been refused something they might
 * reasonably ask for — they have followed a link that was never meant for them,
 * and a page explaining the platform portal exists would be telling them about a
 * surface they have no business knowing about.
 *
 * Not the enforcement point. Every `/api/v1/admin/*` route checks platform staff
 * and a specific platform permission on the server; this only decides what is
 * worth rendering.
 */
export function RequirePlatformStaff({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const session = useAuthStore((s) => s.session);

  if (status === "loading") return <FullScreenSpinner />;
  if (status === "anonymous") return <Navigate to="/login" replace />;
  if (!session?.user?.isPlatformStaff) return <Navigate to={homePathFor(session)} replace />;

  return <>{children}</>;
}

export function RequireAnonymous({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const session = useAuthStore((s) => s.session);

  if (status === "loading") return <FullScreenSpinner />;
  if (status === "authenticated")
    return <Navigate to={homePathFor(session)} replace />;

  return <>{children}</>;
}

/**
 * Gate a screen on a permission.
 *
 * Shows a plain explanation rather than a redirect: bouncing someone to the
 * dashboard for clicking a link leaves them wondering whether the click
 * registered.
 */
export function RequirePermission({
  permission,
  supportPermission,
  children,
}: {
  permission: string;
  /** Checked instead during a SmartSpaza support view. See `NavRoute`. */
  supportPermission?: string | undefined;
  children: ReactNode;
}) {
  const can = useAuthStore((s) => s.can);
  const inSupportView = useAuthStore((s) => Boolean(s.session?.supportView));
  const needed = (inSupportView && supportPermission) || permission;

  if (!can(needed)) {
    return (
      <Card flush>
        <EmptyState
          title="You do not have access to this"
          description="Ask the shop owner if you need it. They can change what your role is allowed to do in Settings."
        />
      </Card>
    );
  }

  return <>{children}</>;
}

function FullScreenSpinner() {
  return (
    <div className="grid min-h-screen place-items-center bg-canvas">
      <span
        role="status"
        aria-label="Loading"
        className="size-8 animate-spin rounded-full border-[3px] border-line border-t-brand-500"
      />
    </div>
  );
}
